import { extractComponentFromChunk, parseComponents } from '@/ui/utils/tiles';
import { componentByTypename } from '@/ui/utils/componentByTypename';
import {
  ContentChunkFragment,
  PieceFragment,
} from '@/integrations/crystallize/__gql-codegen-types/catalogue/graphql';
import { CrystallizeProductData } from '@/ui/components/pages/ProductDetail/types/CrystallizeProductData';

/**
 * Searches through external-area-link chunks for a link matching the current area.
 * The "area" component is an ItemRelationsContent linking to a Crystallize area document.
 * We match the area document's ID against the current area's ID from localization context.
 */
export function findExternalAreaLinkForArea(
  externalAreaLinkChunk: ContentChunkFragment | undefined,
  currentAreaId: string,
): string | undefined {
  const chunks = externalAreaLinkChunk?.chunks;
  if (!chunks) return undefined;

  for (const chunk of chunks) {
    const areaItems = extractComponentFromChunk(chunk, 'area', 'ItemRelationsContent')?.items;
    const areaDocId = areaItems?.[0]?.id;
    const link = extractComponentFromChunk(chunk, 'external-link', 'SingleLineContent')?.text;
    if (areaDocId && link && areaDocId === currentAreaId) {
      return link;
    }
  }

  return undefined;
}

/**
 * Searches through landing page tiles for any tile with a CTA that has a matching
 * external-area-link. Used server-side in ProductDetailPage to pre-compute the external area link.
 */
export function findExternalAreaLinkInTiles(
  data: CrystallizeProductData,
  currentAreaId: string,
): string | null {
  const tiles = componentByTypename(
    data.landingPageTiles?.content,
    'ItemRelationsContent',
  )?.items;

  if (!tiles) return null;

  for (const tile of tiles) {
    if (!('components' in tile)) continue;

    const parsed = parseComponents(tile.components);
    const ctaPiece = parsed['cta'] as PieceFragment | undefined;
    if (!ctaPiece) continue;

    const chunk = extractComponentFromChunk(
      ctaPiece?.components,
      'external-area-link',
      'ContentChunkContent',
    ) as ContentChunkFragment | undefined;
    const link = findExternalAreaLinkForArea(chunk, currentAreaId);
    if (link) return link;
  }

  return null;
}
